
import { UserProfile, MatchCompatibility } from '@/types';
import spotifyService from '@/services/spotifyService';
import { EnhancedUserProfile } from '../types';
import { calculateNewMatchScore } from '../utils/matchScoreUtils';
import { toast } from '@/hooks/use-toast';

/**
 * Enhances user profiles with Spotify data and calculates compatibility
 */
export async function enhanceProfilesWithSpotifyData(
  mockProfiles: EnhancedUserProfile[]
): Promise<EnhancedUserProfile[]> {
  try {
    // Create a copy of the profiles to modify
    const enhancedProfiles = [...mockProfiles];
    
    // For each profile, fetch Spotify data
    const currentUserProfile = enhancedProfiles[0]; // Assuming the first profile is the current user
    let currentUserSpotifyProfile = null;
    
    // First, get the current user's Spotify profile
    const currentUserSpotifyData = await spotifyService.getArtistProfile('1');
    if (currentUserSpotifyData) {
      currentUserProfile.spotifyProfile = currentUserSpotifyData;
      currentUserSpotifyProfile = currentUserSpotifyData;
      console.log("Current user Spotify profile loaded:", currentUserProfile.name);
    }
    
    // Enhanced profiles with Spotify data and compatibility
    for (let i = 0; i < enhancedProfiles.length; i++) {
      const profile = enhancedProfiles[i];
      
      // Skip the current user
      if (i === 0) continue;
      
      console.log(`Fetching Spotify data for ${profile.name}, userId: ${profile.userId}`);
      const spotifyData = await spotifyService.getArtistProfile(profile.userId);
      if (spotifyData) {
        console.log(`Spotify data found for ${profile.name}`);
        
        // Add Spotify data to the profile
        profile.spotifyProfile = spotifyData;
        
        // If both profiles have Spotify data, calculate musical compatibility
        if (currentUserSpotifyProfile && profile.spotifyProfile) {
          const musicalCompatibility = spotifyService.calculateMusicalCompatibility(
            currentUserSpotifyProfile,
            profile.spotifyProfile
          );
          
          // Ensure match score exists before trying to update it
          if (!profile.matchScore) {
            profile.matchScore = {
              score: 0,
              factors: {
                genreOverlap: 0,
                roleCompatibility: 0,
                popularityMatch: 0,
                locationProximity: 0
              },
              compatibilityReasons: []
            };
          }
          
          if (musicalCompatibility && profile.matchScore) {
            // Update match score with musical compatibility
            profile.matchScore.factors.musicalCompatibility = musicalCompatibility;
            
            // Calculate new overall score incorporating musical factors
            const newScore = calculateNewMatchScore(profile.matchScore, musicalCompatibility);
            profile.matchScore.score = newScore;
            
            // Generate compatibility reasons
            profile.matchScore.compatibilityReasons = spotifyService.generateCompatibilityReasons(
              currentUserSpotifyProfile,
              profile.spotifyProfile,
              { name: currentUserProfile.name, role: currentUserProfile.role },
              { name: profile.name, role: profile.role },
              musicalCompatibility
            );
            
            console.log(`Enhanced profile for ${profile.name} with musical compatibility data`);
          }
        }
      } else {
        console.warn(`No Spotify data found for ${profile.name}`);
      }
    }
    
    // Log all profile states to verify data
    enhancedProfiles.forEach(profile => {
      console.log(`Profile ${profile.name} (${profile.userId}):`, {
        hasSpotify: !!profile.spotifyProfile,
        hasMatchScore: !!profile.matchScore,
        score: profile.matchScore?.score || 0
      });
    });
    
    // Sort profiles by match score, excluding the current user
    const sortedProfiles = [
      enhancedProfiles[0],
      ...enhancedProfiles.slice(1).sort((a, b) => (b.matchScore?.score || 0) - (a.matchScore?.score || 0))
    ];
    
    return sortedProfiles;
  } catch (error) {
    console.error('Error enhancing profiles with Spotify data:', error);
    toast({
      title: "Error",
      description: "Could not load Spotify data. Using basic matching instead.",
    });
    return mockProfiles;
  }
}
